import {css} from 'aphrodite';
import React, {useCallback} from 'react';
import {NodeProps} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';

import {NODE_WRAPPER_STYLES, NODE_STYLES} from './BaseSectionedNodeRendererDesignConstants';
import {
  BaseSectionedNodeData,
  BaseSectionedNodeRendererProps,
  NodeValidationState,
  DefaultSectionedNodeRendererNodeData,
  DefaultSectionedNodeRendererNodeDataWithDefaultConfig,
  DefaultSectionedNodeRendererRowData,
  BaseSectionedNodeRendererNodeHandleRendererProps,
  BaseSectionedNodeRendererRowHandleRendererProps,
} from './BaseSectionedNodeRendererTypes';
import {BaseSectionedNodeRendererHeader} from './private-components/BaseSectionedNodeRendererHeader';
import {BaseSectionedNodeRendererSections} from './private-components/BaseSectionedNodeRendererSections';
import {BaseSectionedNodeRendererSectionSummaries} from './private-components/BaseSectionedNodeRendererSectionSummaries';
import {DefaultSectionedNodeRendererNodeHandle} from './private-components/DefaultSectionedNodeRendererNodeHandle';
import {DefaultSectionedNodeRendererSectionRow} from './private-components/DefaultSectionedNodeRendererSectionRow';
import {DefaultSectionedNodeRendererSectionRowHandle} from './private-components/DefaultSectionedNodeRendererSectionRowHandle';

/**
 * Renders a base ReactFlow node with a header, section summaries and sections.
 *
 * This component serves as a starting point for creating custom nodes in ReactFlow,
 * allowing developers to define their own sections and their content while maintaining a consistent
 * design, style and interaction patterns across Benchling applications.
 *
 * See DefaultSectionedNodeRenderer for an out-of-the-box implementation of this component with a built-in
 * row renderer.
 *
 * See BaseSectionedNodeRenderer.stories.tsx in storybook for examples of how this component can change displays.
 * See JellyfishSectionedNodeRenderer.stories.tsx in storybook for examples of how to extend this base class.
 */
export function BaseSectionedNodeRenderer<TRowData, TNodeData extends BaseSectionedNodeData<TRowData>>(
  props: BaseSectionedNodeRendererProps<TRowData, TNodeData>
): JSX.Element {
  return (
    <div
      className={css(NODE_WRAPPER_STYLES.baseSectionedNodeRendererWrapper)}
      {...getTestProps({
        component: 'BaseSectionedNodeRenderer',
        element: 'root',
      })}
    >
      <div
        className={css(
          NODE_WRAPPER_STYLES.baseSectionedNodeRenderer,
          props.data.nodeValidationState === NodeValidationState.WARNING && NODE_STYLES.warningBorder,
          props.data.nodeValidationState === NodeValidationState.ERROR && NODE_STYLES.errorBorder,
          props.data.disabled && NODE_STYLES.disabled
        )}
        {...getTestProps({
          component: 'BaseSectionedNodeRenderer',
          element: 'baseSectionedNodeRenderer',
        })}
      >
        <BaseSectionedNodeRendererHeader {...props} />
        <BaseSectionedNodeRendererSectionSummaries {...props} />
        <BaseSectionedNodeRendererSections {...props} />
      </div>
    </div>
  );
}

/**
 * An implementation of the BaseSectionedNodeRenderer that uses the DefaultSectionedNodeRendererSectionRow
 * for easy out-of-the-box functionality for rendering rows.
 */
export default function DefaultSectionedNodeRenderer<TNodeData extends DefaultSectionedNodeRendererNodeData>(
  props: NodeProps<TNodeData>
): JSX.Element {
  const {nodeHeaderHandleRenderer, nodeHeaderHandleIdGenerator, nodeHeaderHandleAdditionalProps} =
    props.data.nodeHandleRendererCustomizations ?? {};

  const {rowRenderer, rowHandleRenderer, rowHandleIdGenerator, rowHandleAdditionalProps} =
    props.data.rowRendererCustomizations ?? {};

  /** Allow developers to use the default handle with a custom ID generator */
  const buildDefaultNodeHeaderHandleRenderer = useCallback(
    (nodeHeaderHandleProps: BaseSectionedNodeRendererNodeHandleRendererProps): JSX.Element => (
      <DefaultSectionedNodeRendererNodeHandle
        nodeId={nodeHeaderHandleProps.nodeId}
        handleType={nodeHeaderHandleProps.handleType}
        nodeHeaderHandleIdGenerator={nodeHeaderHandleIdGenerator}
        additionalProps={
          nodeHeaderHandleAdditionalProps?.(nodeHeaderHandleProps.nodeId, nodeHeaderHandleProps.handleType) ??
          {}
        }
      />
    ),
    [nodeHeaderHandleIdGenerator, nodeHeaderHandleAdditionalProps]
  );

  /** Allow developers to use the default handle with a custom ID generator */
  const buildDefaultRowHandleRenderer = useCallback(
    (
      rowHandleProps: BaseSectionedNodeRendererRowHandleRendererProps<DefaultSectionedNodeRendererRowData>
    ): JSX.Element => (
      <DefaultSectionedNodeRendererSectionRowHandle
        rowData={rowHandleProps.rowData}
        handleType={rowHandleProps.handleType}
        rowHandleIdGenerator={rowHandleIdGenerator}
        additionalProps={rowHandleAdditionalProps?.(rowHandleProps.rowData, rowHandleProps.handleType) ?? {}}
      />
    ),
    [rowHandleIdGenerator, rowHandleAdditionalProps]
  );

  /** Allow developers to use the default row renderer with optionally customized row handle renderers */
  const buildDefaultRowRenderer = useCallback(
    (rowData: DefaultSectionedNodeRendererRowData): JSX.Element => (
      <DefaultSectionedNodeRendererSectionRow
        {...rowData}
        rowHandleRenderer={rowHandleRenderer ?? buildDefaultRowHandleRenderer}
      />
    ),
    [buildDefaultRowHandleRenderer, rowHandleRenderer]
  );

  const renderers = {
    rowRenderer: rowRenderer ?? buildDefaultRowRenderer,
    nodeHeaderHandleRenderer: nodeHeaderHandleRenderer ?? buildDefaultNodeHeaderHandleRenderer,
  };

  /** Convert DefaultSectionedNodeRenderer node data into BaseSectionedNodeRenderer shapes */
  const propsWithDefaultRowRenderer: NodeProps<DefaultSectionedNodeRendererNodeDataWithDefaultConfig> = {
    ...props,
    data: {
      ...props.data,
      ...renderers,
    },
  };

  return (
    <BaseSectionedNodeRenderer<
      DefaultSectionedNodeRendererRowData,
      DefaultSectionedNodeRendererNodeDataWithDefaultConfig
    >
      {...propsWithDefaultRowRenderer}
    />
  );
}
